import {get, getDatabase, ref, set, remove, off} from "firebase/database";
import {debounce, throttle} from "lodash";
import {Chat, Group, Message, Ping, SidebarItem, Workspace, WorkspaceMember} from './types';
import {User} from "firebase/auth";

export const dbKey = "alpha-nima/"

export async function addChat(chat: Chat) {
  const db = getDatabase();
  await set(ref(db, dbKey+'chats/' + chat.id), chat);
  if (chat.groupPermissions) {
    Object.keys(chat.groupPermissions).forEach((groupId) => {
      const now = Date.now();
      console.log("setting last message for group", groupId, now)
      set(ref(db, dbKey+'activity/' + chat.authorId + '/groups/' + groupId + "/lastMessage"), now);
    })
  }
}

export async function editChatTitle(chat: Chat, newTitle: string) {
  const db = getDatabase();
  await set(ref(db, dbKey+'chats/' + chat.id + '/title'), newTitle);
}

export function getUsers(callback: (snapshot: any) => void) {
  const db = getDatabase();
  const usersRef = ref(db, dbKey+'users');
  //load users once from firebase and then execute callback on the keys of the users ref
  get(usersRef).then((snapshot) => {
    callback(snapshot)
  })
}

export async function viewGroup(me: User, group: Group) {
  const db = getDatabase();
  await set(ref(db, dbKey+'activity/' + me.uid + '/groups/' + group.id + "/lastView"), Date.now());
}

export async function viewSidebarItem(me: User, item: SidebarItem) {
  const db = getDatabase();
  await set(ref(db, dbKey+'activity/' + me.uid + '/sidebars/' + item.id + "/lastView"), Date.now());
}

const throttleViewChat = throttle(async (me: User, chat: Chat) => {
  const db = getDatabase();
  await set(ref(db, dbKey+'activity/' + me.uid + '/chats/' + chat.id + "/lastView"), Date.now());
  await remove(ref(db, dbKey+'unread/' + me.uid + `/${chat.id}`));
}, 300);

export async function viewChat(me: User, chat: Chat) {
  await throttleViewChat(me, chat);
}

export async function viewPings(me: User) {
  const db = getDatabase();
  await set(ref(db, dbKey+'activity/' + me.uid + '/pings/lastView'), Date.now());
}
export async function  addMessage(message: Message, me: User, chat: Chat) {
  const db = getDatabase();
  message.editedAt = message.timestamp;
  await set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id), message);
  await set(ref(db, dbKey+'activity/' + me.uid + '/chats/' + message.chatId + '/lastMessage'), Date.now())
  if (chat.groupPermissions) {
    Object.keys(chat.groupPermissions).forEach((groupId) => {
      const now = Date.now();
      set(ref(db, dbKey+'activity/' + message.authorId + '/groups/' + groupId + "/lastMessage"), now);
    })
  }
}

export async function addPing(ping: Ping, me: User) {
  const db = getDatabase();
  await set(ref(db, dbKey+'pings/' + ping.id), ping);
}

export async function addThread(message: Message, chat: Chat) {
  const db = getDatabase();
  await set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id + "/threads"), [
    ...(message.threads || []),
    chat
  ]);
  await set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id+'/editedAt'), Date.now())
}

export function editMessage(message: Message) {
  const db = getDatabase();
  message.editedAt = Date.now();
  set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id), message);
}

export async function react(message: Message, me: User, reaction: string) {
  const db = getDatabase();
  await set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id+'/reactions/'+reaction+'/'+me.uid), Date.now())
  await set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id+'/editedAt'), Date.now())
}

export async function reactPing(ping: Ping, me: User, reaction: string) {
  const db = getDatabase();
  await set(ref(db, dbKey+'pings/' + ping.id + '/reactions/'+reaction+'/'+me.uid), Date.now())
  await set(ref(db, dbKey+'pings/' + ping.id + '/lastAction'), Date.now())
}

export function removeReaction(message: Message, me: User, reaction: string) {
  const db = getDatabase();
  remove(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id+'/reactions/'+reaction+'/'+me.uid))
  set(ref(db, dbKey+'messages/' + message.chatId + '/' + message.id+'/editedAt'), Date.now())
}

export function removePingReaction(ping: Ping, me: User, reaction: string) {
  const db = getDatabase();
  remove(ref(db, dbKey+'pings/' + ping.id +'/reactions/'+reaction+'/'+me.uid))
  set(ref(db, dbKey+'pings/' + ping.id +'/lastAction'), Date.now())
}

export function removeMessage(message: Message) {
  const db = getDatabase();
  const messageRef = ref(db, dbKey+'messages/' + message.chatId + '/' + message.id);
  remove(messageRef)
}

export function pinToGroup(groupId: string, chatId: string) {
  const db = getDatabase();
  return set(ref(db, dbKey+'pinned/' + groupId + '/' + chatId), Date.now());
}

export function unpinFromGroup(groupId: string, chatId: string) {
  const db = getDatabase();
  return remove(ref(db, dbKey+'pinned/' + groupId + '/' + chatId));
}

export function addGroup(group: Group) {
  const db = getDatabase();
  set(ref(db, dbKey+'groups/' + group.id), group);
}

export async function addWorkspace(workspace: Workspace) {
  const db = getDatabase();
  return set(ref(db, dbKey+'workspaces/' + workspace.id), workspace);
}

export async function joinWorkspace(workspace: Workspace, user: User) {
  const db = getDatabase();
  const myRef = ref(db, dbKey+'workspaces/' + workspace.id + '/members/' + user.uid)
  const me: WorkspaceMember | null = (await get(myRef)).val();
  console.log("Joining");
  console.log(me);
  let avatar = user.photoURL || "";
  if(me && me.avatar) {
    avatar = me.avatar;
  }
  const member: WorkspaceMember = {
    id: user.uid,
    role: "member",
    displayName: user.displayName || "",
    email: user.email || "",
    avatar,
    googlePhotoUrl: avatar,
  }
  set(ref(db, dbKey+'workspaces/' + workspace.id + '/members/' + member.id), member);
}

// Debounced start typing function
const debouncedStartTyping = throttle((user, chat) => {
  console.log("typing")
  const db = getDatabase();
  set(ref(db, dbKey + 'typing/' + chat.id + '/' + user.uid), Date.now());
}, 2000, {
  leading: true,  // Fire immediately
  trailing: false // Don't fire again after the user stops typing
}); // Adjust debounce time for starting typing

// Debounced stop typing function
const debouncedStopTyping = debounce((user, chat) => {
  console.log("stopped typing")
  const db = getDatabase();
  remove(ref(db, dbKey + 'typing/' + chat.id + '/' + user.uid));
}, 2000); // Adjust debounce time for stopping typing (shorter if needed)

export function setTyping(user: User, chat: Chat, typing: boolean = true) {
  if(!typing) {
    debouncedStopTyping(user, chat);
  } else {
    debouncedStartTyping(user, chat);
    debouncedStopTyping(user, chat);
  }
}

export function getRef(name: string) {
  const db = getDatabase();
  return ref(db, dbKey + name);
}

export function lastRead(chatId: string, me: User) {
  const db = getDatabase();
  set(ref(db, dbKey+'users/' + me.displayName + '/' + chatId + '/lastRead'), Date.now())
}

export function lastActive(user: string) {
  const db = getDatabase();
  set(ref(db, dbKey+'lastActive/' + user), Date.now())
}

export async function sendFCMTokenToServer(me: WorkspaceMember, token: string) {
  if (!me?.id || !token) {
    console.warn("Missing user ID or token, skipping FCM token storage.");
    return;
  }
  const db = getDatabase();
  await set(ref(db, dbKey+'fcmTokens/' + me.id + '/tokens/' + token), Date.now());
}

export const removeTokenFromServer = async (userId: string, token: string) => {
  const db = getDatabase();
  const tokenRef = ref(db, dbKey+`fcmTokens/${userId}/tokens/${token}`);
  try {
    await remove(tokenRef);
    console.log("Token removed from server.");
  } catch (error) {
    console.error("Failed to remove token from server:", error);
  }
};